import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { merchantApi } from '../api/config';
import socketService from '../services/socketService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGauge, faUsers, faUniversity, faClipboardList, faMoneyBillTransfer, faSackDollar, faInbox, faChartBar, faCog, faRightFromBracket, faUserTie, faShieldHalved, faArrowRightArrowLeft, faTimes, faWifi, faBars, faHandshake, faClipboardCheck, faChartPie, faLayerGroup, faExclamationTriangle, faSnowflake, faVault, faReceipt, faBan, faCalculator, faCoins, faPercent, faLifeRing, faHourglassHalf, faChevronLeft, faChevronRight, faSun, faMoon, faVolumeHigh, faVolumeXmark, faScaleBalanced, faHandHoldingDollar, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import { fmtBaht } from '../utils/formatMoney';
import { useTranslation } from 'react-i18next';
// import LanguageSwitcher from './LanguageSwitcher';
import soundService from '../services/soundService';
import { useAdminLayoutFees } from '../hooks/useAdminLayoutFees';
import { useSlip2GoBalance } from '../hooks/useSlip2GoBalance';
import { useDarkMode } from '../hooks/useDarkMode';

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [systemLogo, setSystemLogo] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );
  
  const fees = useAdminLayoutFees();
  const slip2go = useSlip2GoBalance();
  const [showSlip2GoModal, setShowSlip2GoModal] = useState(false);
  const { isDark, toggle: toggleDark } = useDarkMode();

  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('admin_sound_enabled') !== 'false');

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    soundService.toggleSound(next);
    localStorage.setItem('admin_sound_enabled', String(next));
  };

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isAdminEmployee = adminUser.type === 'admin_employee';
  const permissions = adminUser.permissions || {};

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setMobileMenuOpen(false);
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isDesktop, mobileMenuOpen]);

  const isCompactSidebar = isDesktop && sidebarCollapsed;

  const closeMobileMenu = () => {
    if (!isDesktop) {
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    closeMobileMenu();
    try {
      await merchantApi.post('/auth/logout');
    } catch (error) {
      // ไม่สำคัญถ้า error
    }
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  if (process.env.NODE_ENV === 'development' && isAdminEmployee) {
    console.log('Admin Employee Permissions:', permissions);
  }

  const refreshPermissions = async () => {
    try {
      const response = await merchantApi.get('/admin/me/refresh');
      
      if (response.data.success) {
        const updatedAdmin = response.data.data;
        localStorage.setItem('admin_user', JSON.stringify(updatedAdmin));
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Permissions refreshed:', updatedAdmin.permissions);
        }

        if (response.data.account_disabled) {
          alert(t('auth.accountSuspended'));
          handleLogout();
        }

        // ⭐ ไม่ reload page เพื่อป้องกันวนลูป
        // localStorage ถูก update แล้ว จะ apply ในครั้งถัดไปที่ user เปลี่ยนหน้า
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        alert(t('auth.accountSuspended'));
        handleLogout();
      }
      console.error('Error refreshing permissions:', error);
    }
  };

  useEffect(() => {
    const handleSafeWalletTransfer = (data: any) => {
      toast.info(
        `💰 Safe Wallet: ${data.merchant_name} โอน ฿${Number(data.amount_thb).toLocaleString()} (ค่าธรรมเนียม ฿${Number(data.fee_amount).toLocaleString()}) → สุทธิ ฿${Number(data.net_thb).toLocaleString()} (${data.usdt_amount} USDT)`,
        { autoClose: 3000 }
      );
    };

    socketService.on('safe-wallet-transfer-created', handleSafeWalletTransfer);

    return () => {
      socketService.off('safe-wallet-transfer-created', handleSafeWalletTransfer);
    };
  }, []);

  useEffect(() => {
    loadSystemLogo();
    socketService.connectAdmin();
    console.log('🔌 Admin Socket.IO initialized');

    if (isAdminEmployee) {
      refreshPermissions();

      const refreshInterval = setInterval(() => {
        refreshPermissions();
      }, 5 * 60 * 1000);

      const handleFocus = () => {
        refreshPermissions();
      };
      window.addEventListener('focus', handleFocus);

      return () => {
        clearInterval(refreshInterval);
        window.removeEventListener('focus', handleFocus);
        socketService.disconnect();
      };
    }

    return () => {
      socketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSystemLogo = async () => {
    try {
      const response = await merchantApi.get('/admin/settings');
      const settings = response.data.data || [];
      const logoSetting = settings.find((s: any) => s.setting_key === 'system_logo_url');
      if (logoSetting && logoSetting.setting_value) {
        setSystemLogo(logoSetting.setting_value);
      }
    } catch (error) {
      console.error('Error loading system logo');
    }
  };

  const allMenuGroups = [
    {
      group: null,
      items: [
        { path: '/admin', icon: faGauge, label: 'Dashboard', exact: true, permission: 'view_dashboard' },
      ],
    },
    {
      group: 'ร้านค้า',
      items: [
        { path: '/admin/merchants', icon: faUsers, label: 'Merchants', permission: 'view_merchants' },
        { path: '/admin/merchant-tiers', icon: faLayerGroup, label: 'ระดับร้านค้า', permission: 'view_merchants' },
        { path: '/admin/banks', icon: faUniversity, label: 'Banks', permission: 'view_banks' },
        { path: '/admin/mismatched-accounts', icon: faExclamationTriangle, label: t('nav.mismatchedAccounts'), permission: 'view_orders' },
        { path: '/admin/bank-suspensions', icon: faBan, label: 'บัญชีถูกระงับ', permission: 'view_banks' },
      ],
    },
    {
      group: 'การเงิน',
      items: [
        { path: '/admin/orders', icon: faClipboardList, label: 'All Orders', permission: 'view_orders' },
        { path: '/admin/transactions', icon: faMoneyBillTransfer, label: 'Transactions', permission: 'view_transactions' },
        { path: '/admin/wallet-transactions', icon: faCoins, label: 'Wallet Transactions', permission: 'view_transactions' },
        { path: '/admin/settlements', icon: faSackDollar, label: 'Settlements', permission: 'view_settlements' },
        { path: '/admin/payouts', icon: faArrowRightArrowLeft, label: 'Payouts', permission: 'view_payouts' },
        { path: '/admin/payout-topups', icon: faSackDollar, label: 'เติมเงิน Payout', permission: 'view_payouts' },
        { path: '/admin/bulk-transfer', icon: faLayerGroup, label: 'Bulk Transfer', permission: 'approve_payouts' },
      ],
    },
    {
      group: 'P2P',
      items: [
        { path: '/admin/p2p-overview', icon: faChartPie, label: 'P2P Overview', permission: 'view_payouts' },
        { path: '/admin/deposit-orders', icon: faReceipt, label: 'P2P Deposits', permission: 'view_payouts' },
        { path: '/admin/p2p-payouts', icon: faArrowRightArrowLeft, label: 'P2P Payouts', permission: 'view_payouts' },
      ],
    },
    {
      group: 'กระเป๋าเงิน',
      items: [
        { path: '/admin/freeze', icon: faSnowflake, label: 'Freeze Wallet', permission: 'view_reports' },
        { path: '/admin/reduce-credit', icon: faHandHoldingDollar, label: 'ลดเครดิต', permission: 'super_admin_only', superAdminOnly: true },
        { path: '/admin/safe-wallet', icon: faVault, label: 'Safe Wallet', permission: 'view_settlements' },
        { path: '/admin/usdt-wallet', icon: faCoins, label: 'USDT Wallet', permission: 'view_settlements' },
        // { path: '/admin/company-transfer', icon: faRightLeft, label: 'โยกเงิน Company', permission: 'approve_payouts' },
      ],
    },
    {
      group: 'ตัวแทน',
      items: [
        { path: '/admin/resellers', icon: faHandshake, label: 'Resellers', permission: 'view_merchants' },
        { path: '/admin/merchant-requests', icon: faClipboardCheck, label: 'Merchant Requests', permission: 'view_merchants' },
        { path: '/admin/reseller-reports', icon: faChartPie, label: 'Reseller Reports', permission: 'view_reports' },
        { path: '/admin/reseller-withdrawals', icon: faMoneyBillTransfer, label: 'Reseller Withdrawals', permission: 'view_reports' },
        { path: '/admin/agent-banks', icon: faUniversity, label: 'Agent Bank', permission: 'super_admin_only', superAdminOnly: true },
        { path: '/admin/agent-commissions', icon: faCoins, label: 'ค่าคอม Agent', permission: 'super_admin_only', superAdminOnly: true },
      ],
    },
    {
      group: 'รายงาน',
      items: [
        { path: '/admin/fee-report', icon: faPercent, label: 'รายงานค่าธรรมเนียม', permission: 'view_reports' },
        { path: '/admin/reports', icon: faChartBar, label: 'Reports', permission: 'view_reports' },
        { path: '/admin/monthly-reports', icon: faChartPie, label: 'Monthly Reports', permission: 'view_reports' },
        { path: '/admin/analytics', icon: faChartBar, label: 'Analytics', permission: 'view_reports' },
        { path: '/admin/reconciliation', icon: faCalculator, label: 'ตัดยอดย้อนหลัง', permission: 'view_reports' },
        { path: '/admin/settlement-calc', icon: faScaleBalanced, label: 'ตัดยอด (คำนวณสด)', permission: 'view_reports' },
        { path: '/admin/bank-capacity', icon: faGaugeHigh, label: 'ความจุบัญชี', permission: 'view_reports' },
      ],
    },
    {
      group: 'Support',
      items: [
        { path: '/admin/tickets', icon: faLifeRing, label: 'Tickets', permission: 'view_dashboard', showAlways: true },
      ],
    },
    {
      group: 'ระบบ',
      items: [
        { path: '/admin/employees', icon: faUserTie, label: 'Employees', permission: 'view_employees' },
        { path: '/admin/webhook-logs', icon: faInbox, label: 'Webhook Logs', permission: 'view_webhooks' },
        { path: '/admin/edit-logs', icon: faClipboardCheck, label: 'Edit Logs', permission: 'view_merchants' },
        { path: '/admin/2fa', icon: faShieldHalved, label: '2FA Security', permission: 'view_dashboard', showAlways: true },
        { path: '/admin/ip-whitelist', icon: faShieldHalved, label: 'IP Whitelist', permission: 'super_admin_only', superAdminOnly: true },
        { path: '/admin/online-users', icon: faWifi, label: 'Online Users', permission: 'super_admin_only', superAdminOnly: true },
        { path: '/admin/settings', icon: faCog, label: 'Settings', permission: 'system_settings' },
      ],
    },
  ];

  const menuGroups = allMenuGroups.map(g => ({
    ...g,
    items: isAdminEmployee
      ? g.items.filter(item => {
          if ((item as any).showAlways) return true;
          if ((item as any).superAdminOnly && (item.path === '/admin/ip-whitelist' || item.path === '/admin/reduce-credit')) return adminUser.role === 'super_admin';
          return permissions[item.permission] === true;
        })
      : g.items.filter(item => {
          if ((item as any).superAdminOnly) {
            // ลดเครดิต = root admin ทุกคนเห็นได้ (ไม่จำกัดเฉพาะ username ngx)
            if (item.path === '/admin/reduce-credit') return true;
            return adminUser.username === 'ngx';
          }
          return true;
        }),
  })).filter(g => g.items.length > 0);

  return (
    <div className="flex min-h-screen bg-slate-100 dark:bg-slate-900">
      <div
        className={`fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={closeMobileMenu}
      />

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex w-72 flex-col bg-white shadow-2xl
          dark:bg-slate-800 dark:shadow-slate-900/60
          transition-transform duration-300 ease-in-out lg:transition-all lg:shadow-none
          lg:border-r lg:border-gray-200/70 dark:lg:border-slate-700/70
          ${isCompactSidebar ? 'lg:w-20' : 'lg:w-72'}
          ${mobileMenuOpen || isDesktop ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Floating collapse handle — Desktop only */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 z-50 hidden lg:flex
                     h-6 w-6 items-center justify-center
                     rounded-full bg-white border border-gray-200 shadow-md
                     dark:bg-slate-700 dark:border-slate-600
                     text-gray-400 hover:text-gray-700 hover:border-gray-300 hover:shadow-lg hover:scale-110
                     dark:text-slate-400 dark:hover:text-slate-200 dark:hover:border-slate-500
                     transition-all duration-200"
          title={sidebarCollapsed ? 'ขยาย sidebar' : 'ย่อ sidebar'}
        >
          <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} className="text-[8px]" />
        </button>
        {/* Top gradient accent strip */}
        <div className="h-[3px] bg-gradient-primary flex-shrink-0" />

        {/* Header / Logo Section */}
        <div
          className={`relative px-4 pt-5 pb-4 border-b border-gray-100 dark:border-slate-700 ${isCompactSidebar ? 'text-center' : ''}`}
        >
          {/* Close - Mobile */}
          <button
            type="button"
            onClick={closeMobileMenu}
            className="absolute top-[18px] right-3 flex h-7 w-7 items-center justify-center rounded-lg
                       text-gray-400 hover:text-gray-600 hover:bg-gray-100
                       transition-all duration-200 lg:hidden"
            aria-label="Close navigation menu"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>

          {!isCompactSidebar ? (
            <>
              {systemLogo ? (
                <img
                  src={systemLogo}
                  alt="Logo"
                  className="max-h-16 w-auto max-w-full object-contain mb-4"
                />
              ) : (
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold text-[15px]"
                    style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
                  >G</div>
                  <div>
                    <h1 className="text-gray-800 dark:text-slate-100 font-semibold text-[15px] tracking-tight leading-none">Go2Pay</h1>
                    <p className="text-[9px] font-medium uppercase tracking-[0.15em] mt-[3px] text-gray-400 dark:text-slate-500">
                      Admin Center
                    </p>
                  </div>
                </div>
              )}

              {/* User Info Card */}
              <div className="rounded-xl overflow-hidden bg-gray-50 border border-gray-100/80 dark:bg-slate-700/50 dark:border-slate-700/80">
                <div className="h-[2px] bg-gradient-primary" />
                <div className="flex items-center gap-2.5 p-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 font-semibold text-xs flex-shrink-0 bg-gray-200 dark:bg-slate-600 dark:text-slate-300">
                    {(adminUser.username || adminUser.full_name || 'A').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate leading-none mb-[5px] text-gray-700 dark:text-slate-200">
                      {adminUser.username || adminUser.full_name || 'Administrator'}
                    </p>
                    <p className="text-[10px] flex items-center gap-1.5 leading-none text-gray-400 dark:text-slate-500">
                      <span
                        className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0"
                        style={{ backgroundColor: '#10b981' }}
                      />
                      {isAdminEmployee ? 'Employee' : 'Super Admin'}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div
              className="w-9 h-9 mx-auto rounded-xl flex items-center justify-center text-white font-bold text-[15px] mt-5"
              style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
            >G</div>
          )}
        </div>

        {/* Navigation */}
        <nav
          className={`flex-1 py-3 overflow-y-auto ${isCompactSidebar ? 'px-1' : 'px-2.5 custom-scrollbar-light'}`}
          style={isCompactSidebar ? { scrollbarWidth: 'none' } : undefined}
        >
          <div className="space-y-4">
            {menuGroups.map((group, gi) => (
              <div key={gi}>
                {/* Group Label */}
                {group.group && !isCompactSidebar && (
                  <div className="flex items-center gap-2 px-1 mb-2">
                    <div
                      className="w-3 h-[1.5px] rounded-full flex-shrink-0"
                      style={{ background: 'var(--theme-primary)', opacity: 0.5 }}
                    />
                    <p className="text-[9.5px] font-bold uppercase tracking-[0.12em] leading-none text-gray-400 dark:text-slate-600">
                      {group.group}
                    </p>
                    <div className="flex-1 h-[1px] rounded-full bg-gray-100 dark:bg-slate-700" />
                  </div>
                )}
                {group.group && isCompactSidebar && (
                  <div className="mx-3 mb-2 h-[1px] bg-gray-100 dark:bg-slate-700" />
                )}

                <div className="space-y-[2px]">
                  {group.items.map((item) => {
                    const isActive = (item as any).exact
                      ? location.pathname === item.path
                      : location.pathname.startsWith(item.path);

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        title={isCompactSidebar ? item.label : undefined}
                        onClick={closeMobileMenu}
                        className={`
                          flex items-center gap-3 py-2.5 rounded-xl text-sm relative group
                          transition-all duration-200
                          ${isCompactSidebar ? 'justify-center px-0' : 'pl-3.5 pr-3'}
                          ${isActive
                            ? 'font-medium text-white'
                            : 'font-normal text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700/50'
                          }
                        `}
                        style={isActive ? {
                          background: 'var(--theme-primary-gradient)',
                          boxShadow: 'var(--theme-shadow-primary)',
                        } : undefined}
                      >
                        {/* Icon */}
                        <span className={`text-[13px] flex-shrink-0 transition-colors duration-200 ${
                          isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-500 dark:text-slate-500 dark:group-hover:text-slate-400'
                        }`}>
                          <FontAwesomeIcon icon={item.icon} />
                        </span>

                        {!isCompactSidebar && (
                          <span className="truncate">{item.label}</span>
                        )}

                        {/* Compact mode tooltip */}
                        {isCompactSidebar && (
                          <div
                            className="absolute left-full ml-3 px-3 py-1.5 text-gray-700 bg-white text-xs rounded-lg
                                       dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600
                                       opacity-0 invisible group-hover:opacity-100 group-hover:visible
                                       transition-all duration-200 whitespace-nowrap z-50 shadow-lg border border-gray-200"
                          >
                            {item.label}
                            <div className={`absolute left-0 top-1/2 -translate-x-[7px] -translate-y-1/2 border-[5px] border-transparent ${isDark ? 'border-r-slate-700' : 'border-r-white'}`} />
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </nav>

        {/* Bottom: Logout */}
        <div className="px-2.5 pb-3 pt-2.5 border-t border-gray-100 dark:border-slate-700">
          <button
            onClick={handleLogout}
            title={isCompactSidebar ? t('common.logout') : undefined}
            className={`
              w-full py-2.5 px-3.5 rounded-xl text-sm font-medium
              flex items-center gap-3 transition-all duration-200 cursor-pointer
              border border-gray-200 bg-gray-50 text-gray-400
              hover:text-red-500 hover:bg-red-50 hover:border-red-200
              dark:border-slate-600 dark:bg-slate-700/30 dark:text-slate-500
              dark:hover:text-red-400 dark:hover:bg-red-900/20 dark:hover:border-red-800
              ${isCompactSidebar ? 'justify-center' : ''}
            `}
          >
            <FontAwesomeIcon icon={faRightFromBracket} />
            {!isCompactSidebar && <span>{t('common.logout')}</span>}
          </button>

          {!isCompactSidebar && (
            <p className="text-center text-[9.5px] mt-2.5 tracking-wide text-gray-300 dark:text-slate-700">
              Go2Pay Admin v2.0
            </p>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        ml-0 flex-1 min-w-0 min-h-screen overflow-x-clip transition-all duration-300
        ${isCompactSidebar ? 'lg:ml-20' : 'lg:ml-72'}
      `}>
        {/* Top Header Bar — ใช้ overflow-x-clip บน main (ไม่ใช่ hidden) ไม่งั้น sticky ไม่ยึด viewport */}
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-gray-200/60 bg-white/80 px-4 py-3 backdrop-blur-md sm:px-6 dark:border-slate-700/60 dark:bg-slate-800/80">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200/70 text-gray-600 transition-colors hover:bg-gray-50 lg:hidden dark:border-slate-700/70 dark:text-slate-400 dark:hover:bg-slate-700/50"
            aria-label="Open navigation menu"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          <div className="hidden items-center gap-2 sm:flex">
            {/* ค่าธรรมเนียมวันนี้ */}
            <div className="flex items-stretch rounded-xl border border-gray-200/80 bg-white/70 backdrop-blur-sm shadow-sm overflow-hidden hover:shadow-md hover:border-gray-300/70 transition-all duration-200 dark:border-slate-700/80 dark:bg-slate-800/70">
              <div className="w-[3px] bg-gradient-to-b from-rose-400 to-orange-400 flex-shrink-0" />
              <div className="flex items-center">
                <div className="flex items-center justify-center px-2.5">
                  <FontAwesomeIcon icon={faReceipt} className="text-rose-400 text-sm" />
                </div>
                <div className="px-2.5 py-2 border-r border-gray-100/80 dark:border-slate-700/80">
                  <p className="text-[9px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-[3px]">วันนี้</p>
                  <p className="text-sm font-bold text-rose-600 leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtBaht(fees.today_fee)}
                  </p>
                </div>
                <div className="hidden md:block px-2.5 py-2 border-r border-gray-100/80 dark:border-slate-700/80">
                  <p className="text-[9px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-[3px]">P.In</p>
                  <p className="text-xs font-bold text-rose-500 leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtBaht(fees.today_payin_fee)}
                  </p>
                </div>
                <div className="hidden md:block px-2.5 py-2 pr-3">
                  <p className="text-[9px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-[3px]">P.Out</p>
                  <p className="text-xs font-bold text-orange-500 leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtBaht(fees.today_payout_fee)}
                  </p>
                </div>
              </div>
            </div>

            {/* Pending Orders รออนุมัติ */}
            <button
              type="button"
              onClick={() => navigate('/admin/orders')}
              className="flex items-stretch rounded-xl border border-gray-200/80 bg-white/70 backdrop-blur-sm shadow-sm overflow-hidden hover:shadow-md hover:border-amber-200/80 hover:-translate-y-px transition-all duration-200 cursor-pointer dark:border-slate-700/80 dark:bg-slate-800/70 dark:hover:border-amber-900/80"
            >
              <div className={`w-[3px] flex-shrink-0 ${(fees.pending_orders ?? 0) > 0 ? 'bg-gradient-to-b from-amber-400 to-yellow-500' : 'bg-gradient-to-b from-gray-300 to-gray-400 dark:from-slate-600 dark:to-slate-700'}`} />
              <div className="flex items-center">
                <div className="flex items-center justify-center px-2.5 relative">
                  <FontAwesomeIcon icon={faHourglassHalf} className={`text-sm ${(fees.pending_orders ?? 0) > 0 ? 'text-amber-500' : 'text-gray-300 dark:text-slate-600'}`} />
                  {(fees.pending_orders ?? 0) > 0 && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  )}
                </div>
                <div className="px-2.5 py-2 pr-3">
                  <p className="text-[9px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-[3px]">รออนุมัติ</p>
                  <p className={`text-sm font-bold leading-none ${(fees.pending_orders ?? 0) > 0 ? 'text-amber-600' : 'text-gray-400 dark:text-slate-600'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {(fees.pending_orders ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </button>

            {/* Slip quota widget (Slip2Go or EasySlip) */}
            {slip2go && (
              <button
                type="button"
                onClick={() => setShowSlip2GoModal(true)}
                className="flex items-stretch rounded-xl border border-gray-200/80 bg-white/70 backdrop-blur-sm shadow-sm overflow-hidden hover:shadow-md hover:border-sky-200/80 hover:-translate-y-px transition-all duration-200 cursor-pointer dark:border-slate-700/80 dark:bg-slate-800/70 dark:hover:border-sky-900/80"
              >
                <div className={`w-[3px] flex-shrink-0 ${(slip2go.tokenRemaining ?? 0) <= 50 ? 'bg-gradient-to-b from-red-400 to-rose-500' : 'bg-gradient-to-b from-sky-400 to-blue-500'}`} />
                <div className="flex items-center">
                  <div className="flex items-center justify-center px-2.5">
                    <span className="text-[10px] font-black text-gray-400 dark:text-slate-500 tracking-tight">
                      {slip2go.provider === 'easyslip' ? 'ESL' : 'S2G'}
                    </span>
                  </div>
                  <div className="px-2.5 py-2">
                    <p className="text-[9px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-[3px]">คงเหลือ</p>
                    <p className="text-xs font-bold leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      <span className={(slip2go.tokenRemaining ?? 0) <= 50 ? 'text-red-500' : 'text-sky-600'}>
                        {(slip2go.tokenRemaining ?? 0).toLocaleString()}
                      </span>
                      {slip2go.tokenLimit !== null ? (
                        <span className="text-gray-300 dark:text-slate-600 text-[10px] font-medium">
                          /{(slip2go.tokenLimit ?? 0).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-gray-300 dark:text-slate-600 text-[10px] font-medium">/∞</span>
                      )}
                    </p>
                  </div>
                  {slip2go.tokenLimit !== null && (
                    <div className="hidden lg:flex items-center pr-3">
                      <div className="w-14 h-1 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${(slip2go.tokenRemaining ?? 0) <= 50 ? 'bg-gradient-to-r from-red-400 to-rose-500' : 'bg-gradient-to-r from-sky-400 to-blue-500'}`}
                          style={{ width: `${Math.min(100, ((slip2go.tokenRemaining ?? 0) / (slip2go.tokenLimit || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Dark mode toggle */}
            <button
              type="button"
              onClick={toggleDark}
              title={isDark ? 'เปลี่ยนเป็น Light Mode' : 'เปลี่ยนเป็น Dark Mode'}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200/70 text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 dark:border-slate-700/70 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            >
              <FontAwesomeIcon icon={isDark ? faSun : faMoon} className="text-sm" />
            </button>
            {/* <LanguageSwitcher /> */}
            <button
              type="button"
              onClick={toggleSound}
              title={soundEnabled ? 'ปิดเสียงแจ้งเตือน' : 'เปิดเสียงแจ้งเตือน'}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200/70 text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 dark:border-slate-700/70 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            >
              <FontAwesomeIcon icon={soundEnabled ? faVolumeHigh : faVolumeXmark} className="text-sm" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 min-h-[calc(100vh-57px)]">
          <Outlet />
        </div>
      </main>

      {/* Slip2Go Package Detail Modal */}
      {showSlip2GoModal && slip2go && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          onClick={() => setShowSlip2GoModal(false)}
        >
          <div
            className={`w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl ${isDark ? 'bg-slate-800 border border-slate-700/60' : 'bg-white border border-black/[0.08]'}`}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`relative px-6 pt-6 pb-4 border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.08) 0%, transparent 70%)' }} />
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-sky-500">
                      {slip2go.provider === 'easyslip' ? 'EasySlip' : 'Slip2Go'}
                    </span>
                  </div>
                  <h2 className="text-slate-800 dark:text-slate-100 font-bold text-lg leading-tight">{slip2go.shopName}</h2>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5"
                    style={{ background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.25)' }}>
                    <span className="text-[10px] font-bold text-orange-500 tracking-wide">{slip2go.package}</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowSlip2GoModal(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-sm" />
                </button>
              </div>
              <p className="mt-3 text-[11px] text-slate-400 dark:text-slate-500">
                หมดอายุ{' '}
                <span className="text-slate-600 dark:text-slate-300 font-medium">
                  {new Date(slip2go.packageExpiredDate).toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </p>
            </div>

            {/* Stats */}
            <div className="px-6 py-5 space-y-4">
              {/* Slip quota */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">โควตาตรวจสลิป</span>
                  <span className="text-xs font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <span className={(slip2go.tokenRemaining ?? 0) <= 50 ? 'text-red-500' : 'text-sky-600'}>
                      {(slip2go.tokenRemaining ?? 0).toLocaleString()}
                    </span>
                    {slip2go.tokenLimit !== null ? (
                      <span className="text-slate-400 dark:text-slate-500"> / {(slip2go.tokenLimit ?? 0).toLocaleString()}</span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500"> / ∞</span>
                    )}
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-700">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: slip2go.tokenLimit !== null
                        ? `${Math.min(100, ((slip2go.tokenRemaining ?? 0) / (slip2go.tokenLimit || 1)) * 100)}%`
                        : '100%',
                      background: (slip2go.tokenRemaining ?? 0) <= 50
                        ? 'linear-gradient(90deg, #f87171, #fb923c)'
                        : 'linear-gradient(90deg, #38bdf8, #818cf8)',
                    }}
                  />
                </div>
              </div>

              {/* QR quota */}
              {slip2go.quotaQrLimit > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">โควตา QR Code</span>
                    <span className="text-xs font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      <span className={(slip2go.quotaQrRemaining ?? 0) <= 10 ? 'text-red-500' : 'text-violet-600'}>
                        {(slip2go.quotaQrRemaining ?? 0).toLocaleString()}
                      </span>
                      <span className="text-slate-400 dark:text-slate-500"> / {(slip2go.quotaQrLimit ?? 0).toLocaleString()}</span>
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${Math.min(100, ((slip2go.quotaQrRemaining ?? 0) / (slip2go.quotaQrLimit || 1)) * 100)}%`,
                        background: (slip2go.quotaQrRemaining ?? 0) <= 10
                          ? 'linear-gradient(90deg, #f87171, #fb923c)'
                          : 'linear-gradient(90deg, #a78bfa, #818cf8)',
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Credit */}
              <div className="flex items-center justify-between py-2.5 rounded-lg px-3 bg-slate-50 border border-slate-100 dark:bg-slate-700/50 dark:border-slate-700">
                <span className="text-xs text-slate-500 dark:text-slate-400">เครดิตคงเหลือ</span>
                <span className="text-xs font-bold text-emerald-600" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {(slip2go.creditRemaining ?? 0).toLocaleString()}
                </span>
              </div>

              {/* Boolean flags */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'ต่ออายุอัตโนมัติ', value: slip2go.autoRenewalPackage },
                  { label: 'ตรวจสลิปด้วยเครดิต', value: slip2go.checkSlipByCredit },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2 rounded-lg px-3 py-2 bg-slate-50 border border-slate-100 dark:bg-slate-700/50 dark:border-slate-700">
                    <div className={`h-2 w-2 rounded-full flex-shrink-0 ${item.value ? 'bg-emerald-400' : 'bg-slate-300 dark:bg-slate-600'}`} />
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }

        .custom-scrollbar-light::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar-light::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar-light::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
        .custom-scrollbar-light::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
      `}</style>
    </div>
  );
};

export default AdminLayout;
